import React, { useState, useEffect, useMemo } from 'react';

/**
 * SeasonalThemeOverlay v2 - Modern, Subtle Seasonal Decorations
 * 
 * A sophisticated overlay system with botanical accents in specific areas.
 * No falling particles - instead uses elegant positioned decorations.
 */

// ============================================
// CONFIGURATION
// ============================================
const THEME_ENABLED = true;

// ============================================
// SEASON & EVENT DETECTION
// ============================================
const EVENTS = {
  christmas: { month: 12, day: 25 },
  newYear: { month: 1, day: 1 },
  easter: { month: 4, day: 20 },
  halloween: { month: 10, day: 31 },
  bastilleDay: { month: 7, day: 14 },
};

const getCurrentSeason = (date = new Date()) => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dateNum = month * 100 + day;

  if (dateNum >= 320 && dateNum <= 620) return 'spring';
  if (dateNum >= 621 && dateNum <= 922) return 'summer';
  if (dateNum >= 923 && dateNum <= 1220) return 'autumn';
  return 'winter';
};

const isEventActive = (eventKey, date = new Date()) => {
  const event = EVENTS[eventKey];
  if (!event) return false;

  const eventDate = new Date(date.getFullYear(), event.month - 1, event.day);
  const oneWeekBefore = new Date(eventDate);
  oneWeekBefore.setDate(oneWeekBefore.getDate() - 7);
  const oneWeekAfter = new Date(eventDate);
  oneWeekAfter.setDate(oneWeekAfter.getDate() + 7);

  if (eventKey === 'newYear') {
    const nyThisYear = new Date(date.getFullYear(), 0, 1);
    const beforeThis = new Date(nyThisYear);
    beforeThis.setDate(beforeThis.getDate() - 7);
    const afterThis = new Date(nyThisYear);
    afterThis.setDate(afterThis.getDate() + 7);
    const nyNextYear = new Date(date.getFullYear() + 1, 0, 1);
    const beforeNext = new Date(nyNextYear);
    beforeNext.setDate(beforeNext.getDate() - 7);
    return (date >= beforeThis && date <= afterThis) || (date >= beforeNext);
  }

  return date >= oneWeekBefore && date <= oneWeekAfter;
};

const getActiveTheme = (date = new Date()) => {
  // Check for active events (1 week before to 1 week after)
  for (const eventKey of Object.keys(EVENTS)) {
    if (isEventActive(eventKey, date)) {
      return eventKey;
    }
  }
  // No active event - return season (which shows nothing since seasons are disabled)
  return getCurrentSeason(date);
};

// ============================================
// SVG DECORATIONS - Elegant botanical line art
// ============================================

// Spring: Cherry blossom branch
const SpringBranchTopRight = () => (
  <svg 
    viewBox="0 0 200 200" 
    className="seasonal-decoration spring-branch-tr"
    style={{
      position: 'fixed',
      top: '60px',
      right: '-10px',
      width: '220px',
      height: '220px',
      opacity: 0.75,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    <defs>
      <linearGradient id="branchGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8B7355" />
        <stop offset="100%" stopColor="#6B5344" />
      </linearGradient>
      <radialGradient id="petalGradient" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFE4EC" />
        <stop offset="100%" stopColor="#F8BBD9" />
      </radialGradient>
    </defs>
    
    {/* Main branch */}
    <path 
      d="M200 0 Q160 40 140 80 Q120 120 100 140 Q80 160 40 180" 
      stroke="url(#branchGradient)" 
      strokeWidth="3" 
      fill="none"
      strokeLinecap="round"
    />
    
    {/* Small branches */}
    <path d="M160 30 Q150 50 130 55" stroke="url(#branchGradient)" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M140 80 Q155 90 165 85" stroke="url(#branchGradient)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    <path d="M110 120 Q95 115 85 125" stroke="url(#branchGradient)" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    
    {/* Cherry blossoms - 5 petal flowers */}
    <g className="blossom blossom-1" style={{ animation: 'gentleSway 4s ease-in-out infinite' }}>
      <circle cx="130" cy="55" r="8" fill="url(#petalGradient)" opacity="0.9" />
      <circle cx="138" cy="50" r="6" fill="url(#petalGradient)" opacity="0.8" />
      <circle cx="125" cy="48" r="5" fill="url(#petalGradient)" opacity="0.7" />
      <circle cx="134" cy="42" r="4" fill="#FFB7C5" opacity="0.6" />
      <circle cx="132" cy="52" r="2" fill="#D4A574" /> {/* Center */}
    </g>
    
    <g className="blossom blossom-2" style={{ animation: 'gentleSway 5s ease-in-out 0.5s infinite' }}>
      <circle cx="165" cy="82" r="7" fill="url(#petalGradient)" opacity="0.85" />
      <circle cx="172" cy="78" r="5" fill="url(#petalGradient)" opacity="0.75" />
      <circle cx="160" cy="76" r="4" fill="url(#petalGradient)" opacity="0.65" />
      <circle cx="168" cy="85" r="2" fill="#D4A574" />
    </g>
    
    <g className="blossom blossom-3" style={{ animation: 'gentleSway 4.5s ease-in-out 1s infinite' }}>
      <circle cx="85" cy="128" r="6" fill="url(#petalGradient)" opacity="0.8" />
      <circle cx="92" cy="124" r="5" fill="url(#petalGradient)" opacity="0.7" />
      <circle cx="80" cy="122" r="4" fill="url(#petalGradient)" opacity="0.6" />
      <circle cx="87" cy="126" r="2" fill="#D4A574" />
    </g>
    
    {/* Floating petals - subtle */}
    <ellipse cx="150" cy="100" rx="4" ry="3" fill="#FFB7C5" opacity="0.4" 
      style={{ animation: 'floatPetal 6s ease-in-out infinite' }} />
    <ellipse cx="110" cy="150" rx="3" ry="2" fill="#FFB7C5" opacity="0.3" 
      style={{ animation: 'floatPetal 7s ease-in-out 1s infinite' }} />
  </svg>
);

// Spring: Small leaf accent bottom left
const SpringLeafBottomLeft = () => (
  <svg 
    viewBox="0 0 120 120" 
    className="seasonal-decoration spring-leaf-bl"
    style={{
      position: 'fixed',
      bottom: '90px',
      left: '-5px',
      width: '120px',
      height: '120px',
      opacity: 0.65,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    <defs>
      <linearGradient id="leafGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#81C784" />
        <stop offset="100%" stopColor="#4CAF50" />
      </linearGradient>
    </defs>
    
    {/* Curved stem */}
    <path 
      d="M0 120 Q30 100 40 70 Q50 40 60 30" 
      stroke="#6B8E23" 
      strokeWidth="2" 
      fill="none"
      strokeLinecap="round"
    />
    
    {/* Leaves */}
    <ellipse cx="45" cy="65" rx="15" ry="8" fill="url(#leafGradient)" opacity="0.8"
      transform="rotate(-30 45 65)"
      style={{ animation: 'leafSway 5s ease-in-out infinite' }} />
    <ellipse cx="55" cy="45" rx="12" ry="6" fill="url(#leafGradient)" opacity="0.7"
      transform="rotate(-45 55 45)"
      style={{ animation: 'leafSway 6s ease-in-out 0.5s infinite' }} />
    <ellipse cx="35" cy="85" rx="10" ry="5" fill="url(#leafGradient)" opacity="0.6"
      transform="rotate(-20 35 85)"
      style={{ animation: 'leafSway 5.5s ease-in-out 1s infinite' }} />
    
    {/* Small flower bud */}
    <circle cx="62" cy="28" r="5" fill="#F8BBD9" opacity="0.7" />
    <circle cx="65" cy="25" r="3" fill="#FFB7C5" opacity="0.6" />
  </svg>
);

// Spring: Butterfly accent (appears occasionally)
const SpringButterfly = () => (
  <svg 
    viewBox="0 0 60 40" 
    className="seasonal-decoration spring-butterfly"
    style={{
      position: 'fixed',
      top: '30%',
      right: '8%',
      width: '50px',
      height: '35px',
      opacity: 0.6,
      pointerEvents: 'none',
      zIndex: 9997,
      animation: 'butterflyFloat 12s ease-in-out infinite',
    }}
  >
    <defs>
      <linearGradient id="wingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#E1BEE7" />
        <stop offset="50%" stopColor="#CE93D8" />
        <stop offset="100%" stopColor="#BA68C8" />
      </linearGradient>
    </defs>
    
    {/* Wings */}
    <ellipse cx="20" cy="18" rx="15" ry="12" fill="url(#wingGradient)" opacity="0.8"
      style={{ transformOrigin: '30px 20px', animation: 'wingFlap 0.4s ease-in-out infinite' }} />
    <ellipse cx="40" cy="18" rx="15" ry="12" fill="url(#wingGradient)" opacity="0.8"
      style={{ transformOrigin: '30px 20px', animation: 'wingFlap 0.4s ease-in-out infinite reverse' }} />
    
    {/* Lower wings */}
    <ellipse cx="22" cy="28" rx="10" ry="8" fill="url(#wingGradient)" opacity="0.6" />
    <ellipse cx="38" cy="28" rx="10" ry="8" fill="url(#wingGradient)" opacity="0.6" />
    
    {/* Body */}
    <ellipse cx="30" cy="20" rx="2" ry="12" fill="#5D4037" />
    
    {/* Antenna */}
    <path d="M29 8 Q25 2 22 0" stroke="#5D4037" strokeWidth="1" fill="none" />
    <path d="M31 8 Q35 2 38 0" stroke="#5D4037" strokeWidth="1" fill="none" />
  </svg>
);

// Summer: Sun rays accent
const SummerSunAccent = () => (
  <svg 
    viewBox="0 0 150 150" 
    className="seasonal-decoration summer-sun"
    style={{
      position: 'fixed',
      top: '80px',
      right: '10px',
      width: '120px',
      height: '120px',
      opacity: 0.3,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    <defs>
      <radialGradient id="sunGradient" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFE082" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#FFB300" stopOpacity="0" />
      </radialGradient>
    </defs>
    
    <circle cx="75" cy="75" r="60" fill="url(#sunGradient)" 
      style={{ animation: 'sunPulse 4s ease-in-out infinite' }} />
    
    {/* Rays */}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
      <line 
        key={i}
        x1="75" y1="75" 
        x2={75 + 70 * Math.cos(angle * Math.PI / 180)} 
        y2={75 + 70 * Math.sin(angle * Math.PI / 180)}
        stroke="#FFB300" 
        strokeWidth="2" 
        opacity="0.4"
        strokeLinecap="round"
      />
    ))}
  </svg>
);

// Summer: Wave/beach accent bottom
const SummerWaveAccent = () => (
  <svg 
    viewBox="0 0 200 60" 
    className="seasonal-decoration summer-wave"
    style={{
      position: 'fixed',
      bottom: '70px',
      left: '0',
      width: '150px',
      height: '45px',
      opacity: 0.25,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    <defs>
      <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#4FC3F7" />
        <stop offset="100%" stopColor="#0288D1" />
      </linearGradient>
    </defs>
    
    <path 
      d="M0 40 Q25 20 50 40 T100 40 T150 40 T200 40 L200 60 L0 60 Z" 
      fill="url(#waveGradient)"
      style={{ animation: 'waveMove 3s ease-in-out infinite' }}
    />
    <path 
      d="M0 45 Q25 30 50 45 T100 45 T150 45 T200 45 L200 60 L0 60 Z" 
      fill="url(#waveGradient)"
      opacity="0.5"
      style={{ animation: 'waveMove 3.5s ease-in-out 0.5s infinite' }}
    />
  </svg>
);

// Autumn: Falling leaves corner
const AutumnLeavesCorner = () => (
  <svg 
    viewBox="0 0 180 180" 
    className="seasonal-decoration autumn-leaves"
    style={{
      position: 'fixed',
      top: '60px',
      right: '-20px',
      width: '160px',
      height: '160px',
      opacity: 0.55,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    <defs>
      <linearGradient id="leaf1Grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FF8A65" />
        <stop offset="100%" stopColor="#E64A19" />
      </linearGradient>
      <linearGradient id="leaf2Grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFB74D" />
        <stop offset="100%" stopColor="#F57C00" />
      </linearGradient>
      <linearGradient id="leaf3Grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#A1887F" />
        <stop offset="100%" stopColor="#6D4C41" />
      </linearGradient>
    </defs>
    
    {/* Maple leaf shapes */}
    <path 
      d="M140 30 L150 45 L165 40 L155 55 L170 65 L150 65 L145 80 L135 65 L115 70 L130 55 L115 50 L135 45 Z" 
      fill="url(#leaf1Grad)"
      style={{ animation: 'leafDrift 6s ease-in-out infinite' }}
    />
    
    <path 
      d="M100 80 L108 92 L120 88 L112 100 L125 108 L108 108 L104 120 L96 108 L80 112 L92 100 L80 94 L96 90 Z" 
      fill="url(#leaf2Grad)"
      style={{ animation: 'leafDrift 7s ease-in-out 1s infinite' }}
      transform="scale(0.8) translate(20, 10)"
    />
    
    <path 
      d="M160 100 L166 110 L176 107 L170 117 L180 123 L166 123 L163 133 L157 123 L145 126 L155 117 L145 112 L157 108 Z" 
      fill="url(#leaf3Grad)"
      style={{ animation: 'leafDrift 5.5s ease-in-out 0.5s infinite' }}
      transform="scale(0.7)"
    />
    
    {/* Acorn */}
    <ellipse cx="130" cy="140" rx="8" ry="10" fill="#8D6E63" />
    <path d="M122 132 Q130 128 138 132 L138 138 Q130 135 122 138 Z" fill="#5D4037" />
  </svg>
);

// Winter: Frost/snowflake corner
const WinterFrostCorner = () => (
  <svg 
    viewBox="0 0 200 200" 
    className="seasonal-decoration winter-frost"
    style={{
      position: 'fixed',
      top: '60px',
      right: '-30px',
      width: '180px',
      height: '180px',
      opacity: 0.4,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    <defs>
      <linearGradient id="frostGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#E3F2FD" />
        <stop offset="100%" stopColor="#90CAF9" />
      </linearGradient>
    </defs>
    
    {/* Elegant snowflake */}
    <g transform="translate(120, 80)" style={{ animation: 'snowflakeRotate 20s linear infinite' }}>
      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
        <g key={i} transform={`rotate(${angle})`}>
          <line x1="0" y1="0" x2="0" y2="-35" stroke="url(#frostGradient)" strokeWidth="2" />
          <line x1="0" y1="-15" x2="-8" y2="-25" stroke="url(#frostGradient)" strokeWidth="1.5" />
          <line x1="0" y1="-15" x2="8" y2="-25" stroke="url(#frostGradient)" strokeWidth="1.5" />
          <circle cx="0" cy="-35" r="3" fill="url(#frostGradient)" />
        </g>
      ))}
      <circle cx="0" cy="0" r="5" fill="url(#frostGradient)" />
    </g>
    
    {/* Smaller snowflakes */}
    <g transform="translate(160, 140) scale(0.5)" style={{ animation: 'snowflakeRotate 15s linear reverse infinite' }}>
      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
        <g key={i} transform={`rotate(${angle})`}>
          <line x1="0" y1="0" x2="0" y2="-25" stroke="url(#frostGradient)" strokeWidth="2" />
          <line x1="0" y1="-12" x2="-5" y2="-18" stroke="url(#frostGradient)" strokeWidth="1" />
          <line x1="0" y1="-12" x2="5" y2="-18" stroke="url(#frostGradient)" strokeWidth="1" />
        </g>
      ))}
    </g>
    
    {/* Frost crystals */}
    <path d="M180 50 L185 55 L180 60 L175 55 Z" fill="#B3E5FC" opacity="0.6" />
    <path d="M150 30 L154 34 L150 38 L146 34 Z" fill="#B3E5FC" opacity="0.5" />
  </svg>
);

// Winter: Bottom frost accent
const WinterFrostBottom = () => (
  <svg 
    viewBox="0 0 200 80" 
    className="seasonal-decoration winter-frost-bottom"
    style={{
      position: 'fixed',
      bottom: '70px',
      left: '0',
      width: '140px',
      height: '60px',
      opacity: 0.3,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    {/* Frost pattern */}
    <path 
      d="M0 80 L20 60 L25 70 L40 50 L50 65 L70 45 L85 60 L100 40 L110 55 L130 35 L140 50 L160 30 L180 45 L200 25" 
      stroke="#90CAF9" 
      strokeWidth="2" 
      fill="none"
      opacity="0.6"
    />
    <path 
      d="M0 80 L15 65 L30 75 L50 55 L65 68 L85 48 L100 62 L120 42 L140 58 L160 38 L180 52 L200 35" 
      stroke="#B3E5FC" 
      strokeWidth="1.5" 
      fill="none"
      opacity="0.4"
    />
  </svg>
);

// Christmas: Holly and lights
const ChristmasDecoration = () => (
  <>
    <svg 
      viewBox="0 0 180 180" 
      className="seasonal-decoration christmas-holly"
      style={{
        position: 'fixed',
        top: '60px',
        right: '-20px',
        width: '150px',
        height: '150px',
        opacity: 0.6,
        pointerEvents: 'none',
        zIndex: 9997,
      }}
    >
      <defs>
        <linearGradient id="hollyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#2E7D32" />
          <stop offset="100%" stopColor="#1B5E20" />
        </linearGradient>
      </defs>
      
      {/* Holly leaves */}
      <path 
        d="M140 40 Q160 50 150 70 Q165 65 170 80 Q155 85 160 100 Q145 90 130 95 Q135 80 120 75 Q135 65 140 40" 
        fill="url(#hollyGradient)"
        style={{ animation: 'gentleSway 5s ease-in-out infinite' }}
      />
      <path 
        d="M120 60 Q135 68 128 85 Q142 82 146 95 Q133 98 136 112 Q122 105 110 108 Q114 95 100 90 Q115 82 120 60" 
        fill="url(#hollyGradient)"
        opacity="0.8"
        style={{ animation: 'gentleSway 6s ease-in-out 0.5s infinite' }}
      />
      
      {/* Berries */}
      <circle cx="145" cy="88" r="6" fill="#C62828" style={{ animation: 'berryPulse 2s ease-in-out infinite' }} />
      <circle cx="138" cy="95" r="5" fill="#D32F2F" style={{ animation: 'berryPulse 2s ease-in-out 0.3s infinite' }} />
      <circle cx="150" cy="96" r="4" fill="#E53935" style={{ animation: 'berryPulse 2s ease-in-out 0.6s infinite' }} />
    </svg>
    
    {/* String lights along top */}
    <div 
      className="seasonal-decoration christmas-lights"
      style={{
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        height: '30px',
        pointerEvents: 'none',
        zIndex: 9997,
        overflow: 'hidden',
      }}
    >
      <svg viewBox="0 0 1200 30" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        {/* Wire */}
        <path 
          d="M0 5 Q100 15 200 5 T400 5 T600 5 T800 5 T1000 5 T1200 5" 
          stroke="#333" 
          strokeWidth="2" 
          fill="none"
        />
        
        {/* Lights */}
        {[50, 150, 250, 350, 450, 550, 650, 750, 850, 950, 1050, 1150].map((x, i) => {
          const colors = ['#F44336', '#4CAF50', '#2196F3', '#FFC107', '#E91E63'];
          return (
            <g key={i}>
              <ellipse 
                cx={x} 
                cy={12} 
                rx="6" 
                ry="10" 
                fill={colors[i % colors.length]}
                style={{ animation: `lightTwinkle 1.5s ease-in-out ${i * 0.2}s infinite` }}
              />
              <ellipse 
                cx={x} 
                cy={12} 
                rx="4" 
                ry="7" 
                fill="white"
                opacity="0.3"
              />
            </g>
          );
        })}
      </svg>
    </div>
  </>
);

// Halloween: Spooky corner
const HalloweenDecoration = () => (
  <svg 
    viewBox="0 0 200 200" 
    className="seasonal-decoration halloween-corner"
    style={{
      position: 'fixed',
      top: '60px',
      right: '-30px',
      width: '180px',
      height: '180px',
      opacity: 0.5,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    {/* Spider web */}
    <g stroke="#9E9E9E" strokeWidth="1" fill="none" opacity="0.6">
      {/* Radial lines */}
      {[0, 30, 60, 90].map((angle, i) => (
        <line 
          key={i}
          x1="200" y1="0" 
          x2={200 - 150 * Math.cos(angle * Math.PI / 180)} 
          y2={150 * Math.sin(angle * Math.PI / 180)}
        />
      ))}
      {/* Spiral */}
      <path d="M200 0 Q180 10 175 25 Q165 20 155 35 Q140 25 130 45 Q110 30 100 55" />
      <path d="M200 0 Q185 15 182 35 Q175 32 168 50 Q155 42 145 65 Q125 50 110 80" />
    </g>
    
    {/* Small spider */}
    <g transform="translate(140, 70)" style={{ animation: 'spiderDangle 4s ease-in-out infinite' }}>
      <ellipse cx="0" cy="0" rx="6" ry="8" fill="#212121" />
      <ellipse cx="0" cy="-10" rx="5" ry="5" fill="#212121" />
      {/* Legs */}
      <path d="M-6 0 Q-15 -5 -18 5" stroke="#212121" strokeWidth="1.5" fill="none" />
      <path d="M6 0 Q15 -5 18 5" stroke="#212121" strokeWidth="1.5" fill="none" />
      <path d="M-5 3 Q-12 8 -15 15" stroke="#212121" strokeWidth="1.5" fill="none" />
      <path d="M5 3 Q12 8 15 15" stroke="#212121" strokeWidth="1.5" fill="none" />
    </g>
    
    {/* Bat */}
    <g transform="translate(80, 120)" style={{ animation: 'batFly 3s ease-in-out infinite' }}>
      <ellipse cx="0" cy="0" rx="4" ry="6" fill="#37474F" />
      <path d="M-4 -2 Q-20 -15 -25 0 Q-15 -5 -4 2" fill="#37474F" style={{ animation: 'wingFlap 0.3s ease-in-out infinite' }} />
      <path d="M4 -2 Q20 -15 25 0 Q15 -5 4 2" fill="#37474F" style={{ animation: 'wingFlap 0.3s ease-in-out infinite reverse' }} />
      {/* Ears */}
      <path d="M-2 -6 L-4 -12 L0 -8" fill="#37474F" />
      <path d="M2 -6 L4 -12 L0 -8" fill="#37474F" />
    </g>
  </svg>
);

// Easter: Pastel eggs and bunny
const EasterDecoration = () => (
  <svg 
    viewBox="0 0 180 180" 
    className="seasonal-decoration easter-corner"
    style={{
      position: 'fixed',
      top: '60px',
      right: '-20px',
      width: '150px',
      height: '150px',
      opacity: 0.55,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    <defs>
      <linearGradient id="egg1Grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F8BBD9" />
        <stop offset="100%" stopColor="#F48FB1" />
      </linearGradient>
      <linearGradient id="egg2Grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#B3E5FC" />
        <stop offset="100%" stopColor="#81D4FA" />
      </linearGradient>
      <linearGradient id="egg3Grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#C8E6C9" />
        <stop offset="100%" stopColor="#A5D6A7" />
      </linearGradient>
    </defs>
    
    {/* Eggs */}
    <ellipse cx="130" cy="70" rx="18" ry="24" fill="url(#egg1Grad)" 
      style={{ animation: 'eggWobble 3s ease-in-out infinite' }} />
    <path d="M115 65 Q130 60 145 65" stroke="#E91E63" strokeWidth="2" fill="none" />
    <path d="M118 75 Q130 80 142 75" stroke="#E91E63" strokeWidth="2" fill="none" />
    
    <ellipse cx="155" cy="110" rx="15" ry="20" fill="url(#egg2Grad)"
      style={{ animation: 'eggWobble 3.5s ease-in-out 0.5s infinite' }} />
    <circle cx="150" cy="105" r="3" fill="#0288D1" opacity="0.6" />
    <circle cx="160" cy="115" r="2" fill="#0288D1" opacity="0.6" />
    <circle cx="152" cy="118" r="2.5" fill="#0288D1" opacity="0.6" />
    
    <ellipse cx="115" cy="130" rx="14" ry="18" fill="url(#egg3Grad)"
      style={{ animation: 'eggWobble 4s ease-in-out 1s infinite' }} />
    <path d="M105 125 L110 130 L105 135 L110 140" stroke="#388E3C" strokeWidth="2" fill="none" />
    <path d="M125 125 L120 130 L125 135 L120 140" stroke="#388E3C" strokeWidth="2" fill="none" />
    
    {/* Small flowers */}
    <g transform="translate(90, 90)">
      <circle cx="0" cy="0" r="4" fill="#FFE082" />
      <circle cx="-5" cy="-3" r="3" fill="#FFF9C4" />
      <circle cx="5" cy="-3" r="3" fill="#FFF9C4" />
      <circle cx="-4" cy="4" r="3" fill="#FFF9C4" />
      <circle cx="4" cy="4" r="3" fill="#FFF9C4" />
    </g>
  </svg>
);

// Bastille Day: Tricolor accent
const BastilleDayDecoration = () => (
  <svg 
    viewBox="0 0 180 180" 
    className="seasonal-decoration bastille-corner"
    style={{
      position: 'fixed',
      top: '50px',
      right: '-10px',
      width: '200px',
      height: '200px',
      opacity: 0.7,
      pointerEvents: 'none',
      zIndex: 9997,
    }}
  >
    {/* Tricolor ribbon */}
    <path d="M180 0 Q150 30 140 80 Q130 130 100 160" stroke="#002395" strokeWidth="12" fill="none" opacity="0.7" />
    <path d="M180 0 Q150 30 140 80 Q130 130 100 160" stroke="#FFFFFF" strokeWidth="8" fill="none" opacity="0.9" />
    <path d="M180 0 Q150 30 140 80 Q130 130 100 160" stroke="#ED2939" strokeWidth="4" fill="none" opacity="0.7" />
    
    {/* Small firework bursts */}
    <g transform="translate(120, 60)" style={{ animation: 'fireworkBurst 2s ease-out infinite' }}>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
        <line 
          key={i}
          x1="0" y1="0" 
          x2={15 * Math.cos(angle * Math.PI / 180)} 
          y2={15 * Math.sin(angle * Math.PI / 180)}
          stroke="#FFD700"
          strokeWidth="2"
          strokeLinecap="round"
        />
      ))}
    </g>
    
    <g transform="translate(90, 110)" style={{ animation: 'fireworkBurst 2s ease-out 0.5s infinite' }}>
      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
        <line 
          key={i}
          x1="0" y1="0" 
          x2={12 * Math.cos(angle * Math.PI / 180)} 
          y2={12 * Math.sin(angle * Math.PI / 180)}
          stroke="#ED2939"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      ))}
    </g>
  </svg>
);

// New Year: Confetti and sparkles
const NewYearDecoration = () => (
  <>
    <svg 
      viewBox="0 0 200 200" 
      className="seasonal-decoration newyear-corner"
      style={{
        position: 'fixed',
        top: '60px',
        right: '-30px',
        width: '180px',
        height: '180px',
        opacity: 0.5,
        pointerEvents: 'none',
        zIndex: 9997,
      }}
    >
      {/* Champagne bottle and glass suggestion */}
      <g transform="translate(130, 100)">
        {/* Glass */}
        <path d="M0 0 L-10 -30 L-8 -32 L8 -32 L10 -30 L0 0" fill="none" stroke="#FFD700" strokeWidth="2" />
        <ellipse cx="0" cy="-32" rx="10" ry="3" fill="#FFD700" opacity="0.3" />
        <line x1="0" y1="0" x2="0" y2="15" stroke="#FFD700" strokeWidth="2" />
        <ellipse cx="0" cy="15" rx="8" ry="2" fill="none" stroke="#FFD700" strokeWidth="2" />
      </g>
      
      {/* Stars and sparkles */}
      <g style={{ animation: 'sparkle 1.5s ease-in-out infinite' }}>
        <path d="M160 40 L163 48 L172 48 L165 53 L168 62 L160 56 L152 62 L155 53 L148 48 L157 48 Z" fill="#FFD700" />
      </g>
      <g style={{ animation: 'sparkle 1.5s ease-in-out 0.3s infinite' }}>
        <path d="M100 70 L102 76 L108 76 L103 80 L105 86 L100 82 L95 86 L97 80 L92 76 L98 76 Z" fill="#FFD700" transform="scale(0.7)" />
      </g>
      <g style={{ animation: 'sparkle 1.5s ease-in-out 0.6s infinite' }}>
        <path d="M180 90 L182 96 L188 96 L183 100 L185 106 L180 102 L175 106 L177 100 L172 96 L178 96 Z" fill="#E0E0E0" transform="scale(0.6)" />
      </g>
      
      {/* Confetti pieces */}
      <rect x="140" y="50" width="6" height="4" fill="#F44336" transform="rotate(25 143 52)" style={{ animation: 'confettiFall 4s ease-in-out infinite' }} />
      <rect x="170" y="70" width="5" height="3" fill="#2196F3" transform="rotate(-15 172 72)" style={{ animation: 'confettiFall 4.5s ease-in-out 0.5s infinite' }} />
      <rect x="120" y="90" width="4" height="3" fill="#4CAF50" transform="rotate(40 122 92)" style={{ animation: 'confettiFall 5s ease-in-out 1s infinite' }} />
      <rect x="155" y="110" width="5" height="3" fill="#FF9800" transform="rotate(-30 157 112)" style={{ animation: 'confettiFall 4.2s ease-in-out 0.3s infinite' }} />
    </svg>
  </>
);

// ============================================
// MAIN COMPONENT
// ============================================
const SeasonalThemeOverlay = () => {
  const [mounted, setMounted] = useState(false);
  const activeTheme = useMemo(() => getActiveTheme(), []);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!THEME_ENABLED || !mounted) {
    return null;
  }

  // Render theme-specific decorations
  const renderDecorations = () => {
    switch (activeTheme) {
      // Seasons removed - only events active
      case 'spring':
      case 'summer':
      case 'autumn':
      case 'winter':
        return null; // No decorations for regular seasons
      case 'christmas':
        return <ChristmasDecoration />;
      case 'halloween':
        return <HalloweenDecoration />;
      case 'easter':
        return <EasterDecoration />;
      case 'bastilleDay':
        return <BastilleDayDecoration />;
      case 'newYear':
        return <NewYearDecoration />;
      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        /* Gentle sway for branches and leaves */
        @keyframes gentleSway {
          0%, 100% { transform: rotate(0deg) translateX(0); }
          50% { transform: rotate(2deg) translateX(3px); }
        }
        
        @keyframes leafSway {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(5deg); }
        }
        
        @keyframes floatPetal {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.4; }
          50% { transform: translate(10px, 15px) rotate(180deg); opacity: 0.2; }
        }
        
        @keyframes butterflyFloat {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-20px, -10px); }
          50% { transform: translate(-10px, 20px); }
          75% { transform: translate(15px, 5px); }
        }
        
        @keyframes wingFlap {
          0%, 100% { transform: scaleX(1); }
          50% { transform: scaleX(0.7); }
        }
        
        @keyframes sunPulse {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.4; }
        }
        
        @keyframes waveMove {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-10px); }
        }
        
        @keyframes leafDrift {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(-5px, 10px) rotate(10deg); }
        }
        
        @keyframes snowflakeRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes berryPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        
        @keyframes lightTwinkle {
          0%, 100% { opacity: 1; filter: brightness(1); }
          50% { opacity: 0.6; filter: brightness(1.5); }
        }
        
        @keyframes spiderDangle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(5px); }
        }
        
        @keyframes batFly {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(10px, -5px); }
          50% { transform: translate(5px, 5px); }
          75% { transform: translate(-5px, -3px); }
        }
        
        @keyframes eggWobble {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(3deg); }
          75% { transform: rotate(-3deg); }
        }
        
        @keyframes fireworkBurst {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        
        @keyframes sparkle {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.2); opacity: 1; }
        }
        
        @keyframes confettiFall {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(20px) rotate(180deg); }
        }
        
        /* Accessibility: Respect reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          .seasonal-decoration * {
            animation: none !important;
          }
        }
        
        /* Hide decorations on very small screens */
        @media (max-width: 640px) {
          .seasonal-decoration {
            opacity: 0.3 !important;
            transform: scale(0.7);
          }
          .christmas-lights {
            display: none;
          }
        }
      `}</style>
      
      <div aria-hidden="true">
        {renderDecorations()}
      </div>
    </>
  );
};

export default SeasonalThemeOverlay;
